import { useState, useEffect } from "react";
import { Bell, BellRing, Check, CheckCheck, Trash2, ExternalLink, Loader2, Rocket, AlertTriangle, Info, Shield, Store, Brain, Server, Zap } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { useI18n } from "@/i18n/I18nContext";
import {
  pushSupported,
  pushPermission,
  fetchPushPublicKey,
  enablePushOnThisDevice,
  type EnablePushResult,
} from "@/services/notificationsService";

const iconMap: Record<string, React.ElementType> = {
  rocket: Rocket,
  warning: AlertTriangle,
  info: Info,
  shield: Shield,
  store: Store,
  brain: Brain,
  server: Server,
  zap: Zap,
};

const typeColors: Record<string, string> = {
  success: "text-nuvra-success",
  warning: "text-nuvra-warning",
  error: "text-destructive",
  info: "text-muted-foreground",
  security: "text-nuvra-warning",
};

export function NotificationPanel() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useI18n();

  // Web Push opt-in state. `available` is true only when the browser supports push
  // AND the server has push configured (VAPID keys). When unavailable we render no
  // push affordance at all, so we never show a control that cannot work.
  const [pushAvailable, setPushAvailable] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(pushPermission());
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    let active = true;
    if (!pushSupported()) { setPushAvailable(false); return; }
    fetchPushPublicKey().then((info) => {
      if (active) setPushAvailable(!!info.configured && !!info.publicKey);
    });
    return () => { active = false; };
  }, []);

  const handleEnablePush = async () => {
    setEnabling(true);
    const result: EnablePushResult = await enablePushOnThisDevice();
    setEnabling(false);
    setPermission(pushPermission());
    if (result === "unconfigured" || result === "unsupported") setPushAvailable(false);
  };

  const handleClick = (n: typeof notifications[0]) => {
    if (!n.is_read) markAsRead(n.id);
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("notif.title")}
          title={t("notif.title")}
          className="text-muted-foreground hover:text-foreground relative h-11 w-11"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-[18px] min-w-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-card border-border">
        <div className="flex items-center justify-between p-3 border-b border-border/40">
          <h4 className="text-sm font-semibold text-foreground">{t("notif.title")}</h4>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground gap-1" onClick={markAllAsRead}>
              <CheckCheck className="h-3 w-3" /> {t("notif.markAllRead")}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">{t("notif.empty")}</div>
          ) : (
            <div className="py-1">
              {notifications.map((n) => {
                const Icon = iconMap[n.icon] || Info;
                return (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-3 py-3 hover:bg-muted/30 transition-colors cursor-pointer group ${!n.is_read ? "bg-primary/5" : ""}`}
                    onClick={() => handleClick(n)}
                  >
                    <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${typeColors[n.type] || "text-muted-foreground"}`} />
                    <div className="flex-1 min-w-0">
                      {/* `items-start` rather than `items-center`: the unread dot must sit on
                          the first line of a title that wraps, not float beside its middle. */}
                      <div className="flex items-start gap-1.5">
                        {/* Clamped, not truncated. A single-line `truncate` cut real notification
                            titles after roughly four words in a 320px panel, so the sentence a
                            notification exists to deliver never reached the reader. Two lines for
                            a title and three for the body hold the copy the product actually
                            writes while still bounding a pathological string. */}
                        <p className={`text-[13px] leading-snug line-clamp-2 ${!n.is_read ? "font-semibold text-foreground" : "text-foreground"}`}>{n.title}</p>
                        {!n.is_read && <div className="h-1.5 w-1.5 rounded-full bg-primary shrink-0 mt-1.5" />}
                      </div>
                      {n.description && <p className="text-xs leading-relaxed text-muted-foreground line-clamp-3 mt-1">{n.description}</p>}
                      <p className="text-[10px] text-muted-foreground mt-1.5">
                        {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {/* Reveal-on-hover is a pointer idiom. A touch device never hovers, so
                        on a phone this made delete permanently unreachable rather than
                        merely quiet. Shown outright where hover does not exist. */}
                    <div className="flex items-center gap-0.5 opacity-100 transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100">
                      {n.link && <ExternalLink className="h-3 w-3 text-muted-foreground" />}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Web Push opt-in: only when the browser supports push AND the server has
            it configured. Honest states: enable, already-on, or blocked. Nothing is
            shown when push cannot work, so there is no dead control. */}
        {pushAvailable && (
          <div className="border-t border-border/40 p-2.5">
            {permission === "granted" ? (
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground px-1">
                <BellRing className="h-3.5 w-3.5 text-primary" /> {t("notif.pushOn")}
              </div>
            ) : permission === "denied" ? (
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground px-1">
                <Bell className="h-3.5 w-3.5" /> {t("notif.pushDenied")}
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full h-8 gap-1.5 text-xs"
                onClick={handleEnablePush}
                disabled={enabling}
              >
                {enabling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />}
                {enabling ? t("notif.pushEnabling") : t("notif.enablePush")}
              </Button>
            )}
          </div>
        )}

        {/* The way out of the popover and into the full Notification Center. The
            popover shows only the newest few; without this the rest of a user's
            history is unreachable. Closes the popover so focus returns to the page. */}
        <div className="border-t border-border/60 p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-8 justify-center text-xs"
            onClick={() => { setOpen(false); navigate("/notifications"); }}
          >
            View all notifications
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
